<script setup>
import { inject, onMounted, computed } from 'vue';
import useCartStore from '../store/cart';
import useOrderStore from '../store/order';
import { useCustomerStore } from '../store/customer';
import { useScheduleStore } from '../store/schedule';
import LineItems from './LineItems.vue';
import OrderTotal from './OrderTotal.vue';
import ModalHead from './ModalHead.vue';
import ModalFooter from './ModalFooter.vue';
import ModalBtn from './ModalBtn.vue';
import Payment from './Payment.vue';
import router from '../routes';
import { useCloverSDK } from '@client/js/composables/useCloverSDK.js';
import { useUberTracking } from '@client/js/composables/useUberTracking.js';

const emit = defineEmits(['close']);

const rootData = inject('rootData');

const { isSdkReady } = useCloverSDK();

const cartStore = useCartStore();
const customerStore = useCustomerStore();
const scheduleStore = useScheduleStore();
const orderStore = useOrderStore();
const address = String(customerStore.isDeliveryOrder ? `${String(customerStore?.street ?? '')}, ${String(customerStore?.city ?? '')}, ${String(customerStore?.state ?? '')}, ${String(customerStore?.zip ?? '')}` : '')

// Uber Direct courier tracking. start() is a no-op unless the site offers Uber
// and we have an order id, so pickup / merchant-fulfilled orders never poll.
const uber = useUberTracking(rootData);
const { status: uberStatus, trackingUrl: uberTrackingUrl, courierName: uberCourier, courierVehicle: uberVehicle } = uber;
const STATUS_LABELS = {
    pending: 'Preparing', pickup: 'Courier on the way to the store',
    pickup_complete: 'Picked up', dropoff: 'Out for delivery',
    delivered: 'Delivered', canceled: 'Canceled', returned: 'Returned',
    failed: 'Delivery failed', uber_failed: 'Arranging delivery',
};
const uberStatusLabel = computed(() => STATUS_LABELS[uberStatus.value] || uberStatus.value);
onMounted(() => {
    if (customerStore.isDeliveryOrder && orderStore.orderId) {
        uber.start(String(orderStore.orderId));
    }
});

// const selectedDaySaved = localStorage.getItem(dateStorageKey);
// const selectedTimeSaved = localStorage.getItem(timeStorageKey);

const returnToMenu = () => {
    // orderStore.orderId = '';
    orderStore.paymentStatus = '';
    orderStore.paymentMessage = '';
    emit('close');
    cartStore.clear();
    router.push('/cart/');
}
</script>

<template>
    <ModalHead></ModalHead>
    <div class="modal__body">
        <div class="thanks">
            <div class="thanks__details">
                <div class="thanks__title">Thank you for your order!</div>
                <template v-if="orderStore.orderId">
                    Order #<span class="thanks__order-id" v-text="orderStore.orderId"></span><br />
                    <div v-if="orderStore.paymentStatus && rootData?.ordering?.flow == 'one'">
                        Payment: <span class="thanks__payment-code" v-if="orderStore?.paymentStatus">{{ orderStore.paymentStatus }}</span>
                        <div class="thanks__payment-details" v-if="orderStore?.paymentMessage">{{ orderStore.paymentMessage }}</div>
                        <div v-show="orderStore.paymentStatus != 'PAID'">
                            <div class="thanks__payment-link">
                                You can check the payment status and complete the payment here: <router-link :to="`/order-payment/${orderStore.orderId}`">Check Payment State</router-link>
                            </div>
                        </div>
                    </div>
                    <br />
                    <div class="thanks__desc">
                        We will call you shortly to confirm the details or your order will be automatically processed.
                    </div>
                    <div class="thanks__tracking" v-if="uberStatus">
                        <div class="thanks__tracking-title">Delivery tracking</div>
                        <div class="thanks__tracking-status">Status: <strong>{{ uberStatusLabel }}</strong></div>
                        <div v-if="uberCourier">Courier: {{ uberCourier }}<span v-if="uberVehicle"> ({{ uberVehicle }})</span></div>
                        <div v-if="uberTrackingUrl" class="thanks__tracking-link">
                            <a :href="uberTrackingUrl" target="_blank" rel="noopener">Track your delivery</a>
                        </div>
                    </div>
                    <div class="thanks__note" v-show="rootData?.ordering?.flow != 'one'">*Please do not change the payment method.</div>
                </template>

            </div>

            <div class="thanks__summary">
                <div class="orderSummary">
                    <div>
                        <strong>Service</strong>: <span class="capitalize">{{ customerStore.orderType }}</span>
                    </div>
                    <div v-show="scheduleStore.formattedSelectedDate && scheduleStore.formattedSelectedTime">
                        <strong>Date</strong>: <span class="capitalize"
                            v-text="scheduleStore.formattedSelectedDate"></span>, <span class="capitalize"
                            v-text="scheduleStore.formattedSelectedTime"></span>
                    </div>
                    <div v-if="customerStore.isDeliveryOrder && address">
                        <strong>Delivery Address</strong>: {{ address?.trim() }}
                    </div>
                    <div v-else>
                        <strong>You can pick up your order at </strong>: {{ rootData.address?.full }}
                    </div>
                    <div v-if="customerStore.paymentOption && rootData?.dualpricing">
                        <strong>Payment Method</strong>: <span class="capitalize"
                            v-text="customerStore.paymentOption"></span>
                    </div>
                </div>
                <div class="orderSummaryHead">
                    <div>Order Summary</div>
                </div>
                <div class="orderSummaryBody">
                    <line-items :controls="false" :hideCash="rootData?.ordering?.flow == 'one'" />
                </div>
            </div>
            <OrderTotal :hideCash="rootData?.ordering?.flow == 'one'" />
            <Payment
                v-if="isSdkReady && customerStore.paymentOption == 'card' && rootData?.ordering?.flow == 'default'" />
        </div>
    </div>
    <ModalFooter>
        <ModalBtn class="btn-primary" @click="returnToMenu()" role="link">Return to menu</ModalBtn>
    </ModalFooter>

</template>

<style lang="scss" scoped>
.payNow {
    border-top: 1px solid rgba(0, 0, 0, .12);
    border-bottom: 1px solid rgba(0, 0, 0, .12);
    margin-left: -1rem;
    margin-right: -1rem;
    padding: 1rem;

    &Head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-weight: 600;
        font-size: 16px;
        margin: 12px 0;
        line-height: 24px;
    }

    margin: 0 -1rem;
    padding: 1rem;
}

.thanks {
    height: 100%;
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;

    &__payment {
        &-code {
            font-weight: 500;
            font-family: monospace;
            padding: 0.125rem 0.375rem;
            display: inline-block;
            background: #f2f2f2;
            border-radius: 4px;
            font-size: 0.875rem;
            border: 1px solid #b1b1b1;
            letter-spacing: -.25px;
            margin-left: 0.25rem;
            margin-right: 0.25rem;
        }
    }

    &__order-id {
        font-weight: 500;
        font-family: monospace;
        padding: 0.125rem 0.375rem;
        display: inline-block;
        background: #f2f2f2;
        border-radius: 4px;
        font-size: 0.875rem;
        border: 1px solid #b1b1b1;
        letter-spacing: -.25px;
        margin-left: 0.25rem;
        margin-right: 0.25rem;
    }

    &__title {
        font-size: 1.125rem;
        font-weight: 700;
        margin-bottom: 0.5rem;
        line-height: 1;
    }

    &__desc {
        font-size: 1rem;
        line-height: 1.2;
        margin-bottom: 1rem;
    }

    &__details {
        padding-bottom: 1rem;
    }

    &__note {
        font-weight: 600;
        font-size: 0.875rem;
    }
}

.capitalize {
    text-transform: capitalize;
}

.cart-total {
    margin-left: -1rem;
    margin-right: -1rem;

}

dl {
    border-top: none;
}

.orderSummary {
    border-top: 1px solid rgba(0, 0, 0, .12);
    border-bottom: 1px solid rgba(0, 0, 0, .12);
    margin-left: -1rem;
    margin-right: -1rem;
    padding: 1rem;

    &Head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-weight: 600;
        font-size: 16px;
        margin: 12px 0;
        line-height: 24px;
    }
}

.modal {
    &__body {
        padding: 1rem;
        padding-bottom: 2rem;
        // min-height: 80vh;
        // min-height: calc(100vh - 170px);
        // min-height: calc(100svh - 170px); // 146

        @media (min-width: 576px) {
            min-height: auto;
        }
    }
}
</style>